import { useCallback } from 'react';
import { always, fromPairs, sortBy, toPairs, tryCatch, uniq } from 'ramda';
import { compact } from '@moda/portal-stanchions';
import { CONFIG } from '../../config';
import { tc } from '../../lib/trackingContext';
import { OrderSummary } from '../useOrderSummary';
import { useCartItems } from '../useCart';
import { getCookies } from '../useCookies';
import { getVariantClickHistory } from '../../lib/variantClicksHistory';

export const useOrderConvertedTracking = () => {
  const { cartItems } = useCartItems();
  const tags = cartItems.map(item => item.attributes.tag);

  const variantClickHistory = compact(
    sortBy(
      item => -(Number(item.attributes.prices.currentPrice.price) * item.attributes.quantity),
      cartItems
    ).map(item => getVariantClickHistory(item.attributes.variantId.toString()))
  )[0];

  const trackOrderConverted = useCallback(
    (order?: OrderSummary) => {
      const variantIds = uniq(cartItems.map(item => item.attributes.variantId));

      const parseSafely = tryCatch(JSON.parse, always({}));

      const sessionParams = parseSafely(getCookies()?.sessionQueryParams ?? '{}');

      // This is to coerce the keys to lowercase, because query params are case insensitive.
      const referralParameters = fromPairs(
        toPairs(sessionParams).map(entry => [entry[0].toLowerCase(), entry[1]])
      );

      tc.track(
        'Order Converted',
        {
          addToScopeOnce: {
            order,
            products: cartItems,
            referralParameters,
            tags
          },
          addToTrackingEvent: {
            eventType: 'conversion',
            index: `Search_${CONFIG.ALGOLIA_SUFFIX}`,
            queryID: variantClickHistory?.queryId,
            objectIDs: variantIds.map(id => `201-${id}`),
            position: variantClickHistory?.position ?? null
          }
        },
        {
          integrations: {
            'Google Analytics': false
          }
        }
      );
    },
    [cartItems, tags, variantClickHistory]
  );

  return { trackOrderConverted };
};
